import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { KeyRound, UserX, UserCheck, Trash2, ChevronDown, Loader2 } from "lucide-react";

const STAFF_ADMINS = new Set(["registered_manager", "administrator", "nominated_individual", "super_admin"]);

// Static config per action kind — no closures over component state, so no stale-state bugs.
const ACTION_CONFIG = {
  reset: {
    title: (s) => `Reset password for ${s.full_name}?`,
    body: "A temporary password will be generated. The staff member will receive an email and must change it on next login.",
    submitLabel: "Reset password",
    danger: false,
    requireReason: false,
    requireAck: false,
  },
  deactivate: {
    title: (s) => `Deactivate ${s.full_name}?`,
    body: "They will be signed out and unable to log in until reactivated. Historical records are retained.",
    submitLabel: "Deactivate",
    danger: false,
    requireReason: true,
    requireAck: false,
  },
  delete: {
    title: (s) => `Permanently delete ${s.full_name}?`,
    body: "All historical visits, attendance records, and audit logs are retained (7-year policy). Jibble link will be removed and sign-ins blocked. This cannot be undone.",
    submitLabel: "Delete staff",
    danger: true,
    requireReason: false,
    requireAck: true,
  },
};

/**
 * Dropdown menu of lifecycle actions for a single staff member.
 * Auth/Permissions PRD v2.0 §3.2 — restricted to RM / Admin / NI / super_admin.
 */
export default function StaffActionsMenu({ staff, onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null); // 'reset' | 'deactivate' | 'delete' | null
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [error, setError] = useState("");
  const [tempResult, setTempResult] = useState(null);

  if (!user || !STAFF_ADMINS.has(user.role)) return null;
  if (user.id === staff.id) return null;
  if (staff.deleted) return null;

  const close = () => {
    setConfirmKind(null);
    setReason("");
    setAcknowledge(false);
    setBusy(false);
    setError("");
  };

  const handleReactivate = async () => {
    if (!window.confirm(`Reactivate ${staff.full_name}? They will be able to log in again.`)) return;
    try {
      await api.post(`/staff/${staff.id}/reactivate`);
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  // Fresh-state submit: reads current `reason` / `acknowledge` at click-time, not at modal-open time.
  const submitConfirm = async () => {
    setError("");
    const cfg = ACTION_CONFIG[confirmKind];
    if (!cfg) return;
    if (cfg.requireReason && (!reason || reason.trim().length < 3)) {
      setError("Please type a reason (minimum 3 characters).");
      return;
    }
    if (cfg.requireAck && !acknowledge) {
      setError("Tick the acknowledgement to continue.");
      return;
    }
    setBusy(true);
    try {
      if (confirmKind === "reset") {
        const { data } = await api.post(`/staff/${staff.id}/reset-password`, { notify_email: true, reason });
        setTempResult({ kind: "reset", staffName: staff.full_name, tempPassword: data.temp_password });
        close();
        onChanged?.();
      } else if (confirmKind === "deactivate") {
        await api.post(`/staff/${staff.id}/deactivate`, { reason: reason.trim() });
        close();
        onChanged?.();
      } else if (confirmKind === "delete") {
        await api.delete(`/staff/${staff.id}`, { params: { confirm: true, reason: reason || "" } });
        close();
        onChanged?.();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "staff_has_active_assignments") {
        setError(`${detail.message} Reassign or delete those shifts first.`);
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(e.message || "Action failed");
      }
      setBusy(false);
    }
  };

  const cfg = confirmKind ? ACTION_CONFIG[confirmKind] : null;

  return (
    <>
      <div className="relative inline-block text-left">
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={`staff-actions-button-${staff.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
        >
          Manage <ChevronDown className="h-3.5 w-3.5" />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
            <div className="absolute right-0 mt-1 w-56 rounded-md border border-slate-200 bg-white shadow-lg z-20 py-1 text-sm">
              <button
                onClick={() => { setOpen(false); setConfirmKind("reset"); }}
                data-testid={`staff-action-reset-${staff.id}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-800"
              >
                <KeyRound className="h-4 w-4 text-amber-600" /> Reset password
              </button>
              {staff.status === "inactive" ? (
                <button
                  onClick={() => { setOpen(false); handleReactivate(); }}
                  data-testid={`staff-action-reactivate-${staff.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-800"
                >
                  <UserCheck className="h-4 w-4 text-emerald-600" /> Reactivate
                </button>
              ) : (
                <button
                  onClick={() => { setOpen(false); setConfirmKind("deactivate"); }}
                  data-testid={`staff-action-deactivate-${staff.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-800"
                >
                  <UserX className="h-4 w-4 text-amber-600" /> Deactivate
                </button>
              )}
              <div className="my-1 border-t border-slate-100" />
              <button
                onClick={() => { setOpen(false); setConfirmKind("delete"); }}
                data-testid={`staff-action-delete-${staff.id}`}
                className="w-full text-left px-3 py-2 hover:bg-red-50 flex items-center gap-2 text-red-700"
              >
                <Trash2 className="h-4 w-4" /> Delete permanently
              </button>
            </div>
          </>
        )}
      </div>

      {cfg && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid={`staff-confirm-${confirmKind}`}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="font-heading text-lg font-bold text-slate-900">{cfg.title(staff)}</h3>
            <p className="mt-2 text-sm text-slate-700">{cfg.body}</p>
            {cfg.requireReason && (
              <div className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Reason</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid={`staff-confirm-reason-${confirmKind}`}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  placeholder="e.g. Staff member has resigned"
                />
                <p className="mt-1 text-[11px] text-slate-500">{reason.trim().length}/3 minimum characters</p>
              </div>
            )}
            {cfg.requireAck && (
              <label className="mt-4 flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledge}
                  onChange={(e) => setAcknowledge(e.target.checked)}
                  data-testid={`staff-confirm-ack-${confirmKind}`}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500/30"
                />
                I understand this action cannot be undone.
              </label>
            )}
            {error && (
              <p className="mt-3 text-sm bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2" data-testid={`staff-confirm-error-${confirmKind}`}>
                {error}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                data-testid={`staff-confirm-cancel-${confirmKind}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitConfirm}
                disabled={busy}
                data-testid={`staff-confirm-submit-${confirmKind}`}
                className={`px-4 py-2 text-sm font-semibold rounded-md text-white disabled:opacity-50 inline-flex items-center gap-2 ${cfg.danger ? "bg-red-600 hover:bg-red-700" : "bg-[#0D7377] hover:bg-[#0A5C5F]"}`}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {cfg.submitLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {tempResult && tempResult.kind === "reset" && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-testid="staff-temp-password-modal">
            <h3 className="font-heading text-lg font-bold text-slate-900">Temporary password generated</h3>
            <p className="mt-2 text-sm text-slate-700">{tempResult.staffName} has been sent an email with these details. Share this password securely if needed.</p>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md font-mono text-sm break-all" data-testid="staff-temp-password-value">
              {tempResult.tempPassword}
            </div>
            <p className="mt-3 text-xs text-slate-500">They will be required to change this password on first login.</p>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setTempResult(null)}
                data-testid="staff-temp-password-close"
                className="px-4 py-2 text-sm font-semibold rounded-md bg-[#0D7377] hover:bg-[#0A5C5F] text-white"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
